import { z } from "zod";

import { DatabaseDefaults } from "@/src/models/database-defaults";

export enum ApprovalType {
  ATHENA_COVERAGE = "athena_coverage",
  // Gates Pinnacle referral closure AND PA East referral-match upload. Both
  // ask ops "we matched this incoming doc to this eCW referral order, OK to
  // proceed with the downstream side-effect?". `closureInputs` inside the
  // metadata is still named for Pinnacle's downstream `referralClosureStep`
  // consumer (PA East populates it best-effort but doesn't read it).
  REFERRAL_MATCH = "referral_match",
  TPR_RECEIVING_ASSIGNMENT = "tpr_receiving_assignment",
  TPR_REFERRAL_OPS = "tpr_referral_ops",
}

export const referralMatchMetadataSchema = z.object({
  instruction: z.string().optional(),
  // CC workflow inputs are persisted for crash-safe idempotency on retries.
  closureInputs: z.object({
    patientMrn: z.string(),
    appointmentDate: z.string(),
    referralStartDate: z.string(),
    referralReasonCode: z.string(),
    assignedTo: z.string().nullable(),
    specialty: z.string().nullish(),
  }),
  evidence: z.object({
    progressNote: z.object({
      clinicName: z.string().nullable(),
      clinicAddress: z.string().nullable().optional(),
      providerName: z.string().nullable().optional(),
      reasonForVisit: z.string().nullable(),
      specialtyFolder: z.string().nullable(),
      dateOfEvent: z.string().nullable(),
      diagnosisCodes: z.array(z.string()),
    }),
    openReferrals: z.array(
      z.object({
        index: z.number(),
        performerName: z.string().nullable(),
        performerAddress: z.string().nullable().optional(),
        specialty: z.string().nullable(),
        reasonText: z.string().nullable(),
        icdCodes: z.array(z.string()),
        startDate: z.string().nullable(),
        endDate: z.string().nullable().optional(),
      })
    ),
  }),
  evaluation: z.object({
    matchedIndex: z.number(),
    reasoning: z.array(z.string()),
  }),
});

export type ReferralMatchMetadata = z.infer<typeof referralMatchMetadataSchema>;

export const athenaCoverageConfirmationMetadataSchema = z.object({
  instruction: z.string(),
  reviewMode: z.enum(["verify", "needs_action"]),
  departmentId: z.string(),
  expectedCoverageCount: z.number().int().positive(),
  expectSelfPay: z.boolean(),
  reviewReasons: z.array(z.string()).default([]),
  expectedCoverages: z.array(
    z.object({
      order: z.number().int().positive(),
      sequence: z.number().int().positive().nullable(),
      label: z.string(),
      insurancePackageId: z.string().nullable(),
      subscriberId: z.string().nullable(),
      casePolicy: z.boolean(),
    })
  ),
  currentCoverages: z.array(
    z.object({
      insuranceId: z.string(),
      insurancePackageId: z.string().nullable(),
      planName: z.string().nullable(),
      sequence: z.number().int().positive().nullable(),
      subscriberId: z.string().nullable(),
      selfPay: z.boolean(),
      casePolicy: z.boolean(),
    })
  ),
});

export type AthenaCoverageConfirmationMetadata = z.infer<typeof athenaCoverageConfirmationMetadataSchema>;

export const tprReceivingAssignmentConfirmationMetadataSchema = z.object({
  instruction: z.string(),
  providerName: z.string().nullable(),
  providerNpi: z.string().nullable(),
  targetDepartmentId: z.string().nullable(),
  reviewReasons: z.array(z.string()),
});

export type TprReceivingAssignmentConfirmationMetadata = z.infer<
  typeof tprReceivingAssignmentConfirmationMetadataSchema
>;

export const tprReferralOpsKindSchema = z.enum([
  "auth_request_upload",
  "authorization_review",
  "close_document",
  "manual_authorization",
  "pcp_referral_fax",
]);
export type TprReferralOpsKind = z.infer<typeof tprReferralOpsKindSchema>;

export const tprReferralOpsMetadataSchema = z.object({
  kind: tprReferralOpsKindSchema,
  instruction: z.string(),
  referralAuthId: z.string().nullable().optional(),
  write: z.enum(["paper", "authorization"]).nullable().optional(),
  pending: z.boolean().optional(),
  appointmentAttached: z.boolean().optional(),
  needsManualAppointmentAttach: z.boolean().optional(),
});
export type TprReferralOpsMetadata = z.infer<typeof tprReferralOpsMetadataSchema>;

type BaseTaskApproval = DatabaseDefaults & {
  taskId: string;
  approved: boolean | null;
  decidedBy: string | null;
};

export type TaskApproval =
  | (BaseTaskApproval & {
      approvalType: ApprovalType.REFERRAL_MATCH;
      metadata: ReferralMatchMetadata | null;
    })
  | (BaseTaskApproval & {
      approvalType: ApprovalType.ATHENA_COVERAGE;
      metadata: AthenaCoverageConfirmationMetadata | null;
    })
  | (BaseTaskApproval & {
      approvalType: ApprovalType.TPR_RECEIVING_ASSIGNMENT;
      metadata: TprReceivingAssignmentConfirmationMetadata | null;
    })
  | (BaseTaskApproval & {
      approvalType: ApprovalType.TPR_REFERRAL_OPS;
      metadata: TprReferralOpsMetadata | null;
    });

export function parseTaskApprovalMetadata(approvalType: ApprovalType, metadata: unknown): TaskApproval["metadata"] {
  switch (approvalType) {
    case ApprovalType.REFERRAL_MATCH: {
      const parsed = referralMatchMetadataSchema.safeParse(metadata);

      return parsed.success ? parsed.data : null;
    }

    case ApprovalType.ATHENA_COVERAGE: {
      const parsed = athenaCoverageConfirmationMetadataSchema.safeParse(metadata);

      return parsed.success ? parsed.data : null;
    }

    case ApprovalType.TPR_RECEIVING_ASSIGNMENT: {
      const parsed = tprReceivingAssignmentConfirmationMetadataSchema.safeParse(metadata);

      return parsed.success ? parsed.data : null;
    }

    case ApprovalType.TPR_REFERRAL_OPS: {
      const parsed = tprReferralOpsMetadataSchema.safeParse(metadata);

      return parsed.success ? parsed.data : null;
    }

    default: {
      const _exhaustive: never = approvalType;

      return _exhaustive;
    }
  }
}
